import React, { useState } from 'react';
import { ChatLines, LightBulb, Copy, Check } from 'iconoir-react';
import { useIceBreakers } from '../../hooks/useMatch';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils';
import { routes } from '../../lib/routes';
import { startDirectMessage } from '../../services/chatServices';
import toast from 'react-hot-toast';
import { Toaster } from '../common';

interface IceBreakerSuggestionsProps {
  matchId: string;
  otherUserId: string;
  className?: string;
}

const IceBreakerSuggestions = ({
  matchId,
  otherUserId,
  className = '',
}: IceBreakerSuggestionsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isStartingChat, setIsStartingChat] = useState(false);
  const navigate = useNavigate();

  // Only fetch when expanded
  const { data: iceBreakersResponse, isLoading } = useIceBreakers(
    matchId,
    isExpanded
  );

  const suggestions = iceBreakersResponse?.result?.ice_breakers?.suggestions || [];
  const selectedSuggestion = iceBreakersResponse?.result?.ice_breakers?.selected_suggestion;

  const handleCopy = async (suggestion: string, index: number) => {
    try {
      await navigator.clipboard.writeText(suggestion);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);

      // Engagement signal only. This does NOT mark the starter "Used" — that
      // happens when the message is actually sent (see ChatInterface).
      api.post('/analytics/track', {
        event_type: 'interest_expressed',
        event_category: 'matching',
        event_data: { match_id: matchId, action: 'ice_breaker_copied', suggestion_index: index },
      }).catch(() => {});
    } catch {
      toast.custom(t => (
        <Toaster message="Failed to copy" type="error" id={t.id} />
      ));
    }
  };

  const handleUseInChat = async (suggestion: string, index: number) => {
    setIsStartingChat(true);
    try {
      // Start chat with the match
      const conversationId = await startDirectMessage(otherUserId, matchId);
      if (conversationId) {
        // Navigate to chat with the suggestion pre-filled. Usage is tracked
        // only when the message is actually sent (see ChatInterface), so the
        // matchId + index ride along in the URL for that to fire.
        navigate(`${routes.chat}?channel=${conversationId}&prefill=${encodeURIComponent(suggestion)}&iceBreakerMatchId=${encodeURIComponent(matchId)}&iceBreakerIndex=${index}`);
      }
    } catch {
      toast.custom(t => (
        <Toaster message="Failed to start chat" type="error" id={t.id} />
      ));
    } finally {
      setIsStartingChat(false);
    }
  };

  return (
    <div className={`${className}`}>
      {/* Expandable Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors w-full"
      >
        <LightBulb className="w-4 h-4" />
        <span>Conversation Starters</span>
        <span
          className={`ml-auto transform transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.5 4.5L6 8L9.5 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-3 space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((suggestion: string, index: number) => (
              <div
                key={index}
                className={`bg-gray-50 rounded-lg p-3 border group hover:border-primary-200 transition-colors ${selectedSuggestion === index ? 'border-green-200 bg-green-50/50' : 'border-gray-100'}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-sm text-body-700 leading-relaxed">
                    "{suggestion}"
                  </p>
                  {selectedSuggestion === index && (
                    <span className="shrink-0 text-[10px] font-semibold text-green-600 bg-green-100 px-1.5 py-0.5 rounded">Used</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleCopy(suggestion, index)}
                    className="flex items-center gap-1 text-xs text-body-400 hover:text-primary-500 transition-colors"
                    title="Copy to clipboard"
                  >
                    {copiedIndex === index ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-green-500" />
                        <span className="text-green-500">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => handleUseInChat(suggestion, index)}
                    disabled={isStartingChat}
                    className="flex items-center gap-1 text-xs text-body-400 hover:text-primary-500 transition-colors disabled:opacity-50"
                    title="Use in chat"
                  >
                    <ChatLines className="w-3.5 h-3.5" />
                    <span>{isStartingChat ? 'Opening...' : 'Use in chat'}</span>
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-body-400 italic py-2">
              No conversation starters available yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default IceBreakerSuggestions;
